<template>
  <div class="w-full flex flex-col flex-grow min-h-0">
  <!-- The code was sent -->
    <AuthCard v-if="view === 'code'" :title="isSms ? 'Check your phone' : 'Check your email'" :icon="isSms ? DeviceMobileIcon : MailIcon">
      <template #subtitle>
        <span v-if="isSms && challenge.maskedDestination">We texted a 6-digit code to <b>{{ challenge.maskedDestination }}</b>.</span>
        <span v-else-if="isSms">We texted a 6-digit code to your mobile phone.</span>
        <span v-else-if="challenge.maskedDestination">We sent a 6-digit code to <b>{{ challenge.maskedDestination }}</b>.</span>
        <span v-else>We sent a 6-digit code to your email.</span>
        <span v-if="expiresIn"> It expires in {{ expiresIn }}.</span>
      </template>
      <form class="flex flex-col gap-5" @submit.prevent="verify" novalidate>
      <!-- Code box -->
        <div class="flex flex-col gap-2">
          <CodeInput id="code" ref="codeInput" v-model="code" :invalid="!!codeError" :disabled="busy" describedby="code-error"
            @update:modelValue="codeError = ''" @complete="verify" />
          <div v-if="codeError" id="code-error" role="alert" class="text-red font-semibold text-center whitespace-normal">{{ codeError }}</div>
        </div>
      <!-- Trust this browser -->
        <div v-if="allowTrust" class="flex flex-row items-start gap-2.5">
          <Checkbox id="trust-device" class="mt-px" :checked="trustDevice" :disabled="busy" aria-describedby="trust-help" @toggle="trustDevice = !trustDevice" />
          <div class="flex flex-col gap-0.5">
            <label for="trust-device" class="font-semibold cursor-pointer">{{ challenge.trustDays ? `Trust this browser for ${challenge.trustDays} days` : 'Trust this browser' }}</label>
            <div id="trust-help" class="text-[11px] opacity-70 whitespace-normal">You won’t be asked for a code on this browser. Don’t use on a shared computer.</div>
          </div>
        </div>
        <button type="submit" class="bn-solid-sky w-full justify-center h-10 sm:h-9 text-sm" :class="{ 'is-busy': busy }" :disabled="busy || code.length !== 6">
          <div>{{ busy ? 'Verifying…' : 'Verify' }}</div>
        </button>
      <!-- Back and resend -->
        <div class="flex flex-row flex-wrap items-center justify-between gap-y-2 -mt-1">
          <button type="button" class="bn-text text-normal" @click="$emit('back')"><ArrowLeftIcon /><div>Back to sign in</div></button>
          <button type="button" class="bn-text" :disabled="resendIn > 0 || busy" @click="resend()"><div>{{ resendLabel }}</div></button>
          <div v-if="isSms" class="w-full flex justify-center">
            <button type="button" class="bn-text text-normal" :disabled="busy" @click="resend('email')"><div>Get the code by email instead</div></button>
          </div>
        </div>
        <div v-if="resendNotice" class="text-center opacity-80 whitespace-normal -mt-2">{{ resendNotice }}</div>
      </form>
    </AuthCard>
  <!-- The code could not be sent -->
    <AuthCard v-else-if="view === 'failed'" title="We couldn’t send a code" :icon="ExclamationCircleOutline" tone="amber">
      <template #subtitle>
        We couldn’t {{ isSms ? 'text' : 'email' }} a code to <b>{{ challenge.maskedDestination || (isSms ? 'your mobile phone' : 'your email') }}</b>.
        Nothing is on its way, so there’s no need to wait.
      </template>
      <div class="flex flex-col gap-3">
        <div v-if="failedError" role="alert" class="flex flex-row items-start gap-2 rounded-md bg-red-500/10 px-3 py-2 text-red font-semibold whitespace-normal">
          <ExclamationCircleIcon class="w-4 h-4 flex-shrink-0 mt-px" />
          <div>{{ failedError }}</div>
        </div>
        <button type="button" class="bn-solid-sky w-full justify-center h-10 sm:h-9 text-sm" :class="{ 'is-busy': busy }" :disabled="busy || resendIn > 0" @click="resend()">
          <div>{{ resendIn > 0 && resendIn < 120 ? `Try again in ${resendClock}` : 'Try again' }}</div>
        </button>
        <button v-if="isSms" type="button" class="w-full justify-center h-10 sm:h-9 text-sm" :disabled="busy" @click="resend('email')"><div>Send it by email instead</div></button>
        <div v-if="resendNotice" class="text-center opacity-80 whitespace-normal">{{ resendNotice }}</div>
        <div class="flex justify-center">
          <button type="button" class="bn-text text-normal" @click="$emit('back')"><ArrowLeftIcon /><div>Back to sign in</div></button>
        </div>
      </div>
    </AuthCard>
  <!-- The whole sign-in has expired -->
    <AuthCard v-else title="Sign-in expired" :icon="ExclamationCircleOutline" tone="amber">
      <template #subtitle>{{ signInExpired }}</template>
      <button type="button" class="w-full justify-center h-10 sm:h-9 text-sm" @click="$emit('back')"><ArrowLeftIcon /><div>Back to sign in</div></button>
    </AuthCard>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import api from '@/api'
import { MailIcon, DeviceMobileIcon, ExclamationCircleIcon as ExclamationCircleOutline } from '@heroicons/vue/outline'
import { ArrowLeftIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import AuthCard from '@/components/auth/AuthCard.vue'
import CodeInput from '@/components/auth/CodeInput.vue'
import Checkbox from '@/components/ui/Checkbox.vue'
import { formatDuration } from '@/components/auth/authText'

// The code card: where the code went, the code box, trust this browser, Resend. Shared by sign-in and (later) the
// reset page. The challenge is the server's; every field is read optionally.
export default {
  name: 'CodeStep',
  components: { AuthCard, CodeInput, Checkbox, ArrowLeftIcon, ExclamationCircleIcon },
  props: {
    challenge: { type: Object, required: true },
    allowTrust: { type: Boolean, default: true }
  },
  emits: ['update:challenge', 'signedIn', 'back'],
  setup(props, { emit }) {

  // Refs
    const code = ref('')
    const codeError = ref('')
    const failedError = ref('')
    const resendNotice = ref('')
    const trustDevice = ref(false)
    const busy = ref(false)
    const signInExpired = ref('')
    const resendIn = ref(0)
    const codeInput = ref(null)
    let countdown = null

  // Computed
    const isSms = computed(() => props.challenge?.channel === 'sms')
    const view = computed(() => signInExpired.value ? 'expired' : props.challenge?.delivery === 'failed' ? 'failed' : 'code')
    const expiresIn = computed(() => formatDuration(props.challenge?.codeExpiresIn))
    const resendClock = computed(() => `${Math.floor(resendIn.value / 60)}:${String(resendIn.value % 60).padStart(2, '0')}`)
    // A long wait (the hourly cap) is explained by the notice, not shown as a long clock.
    const resendLabel = computed(() => resendIn.value > 0 && resendIn.value < 120 ? `Resend in ${resendClock.value}` : 'Resend code')

  // API
    const verify = async () => {
      if (busy.value || code.value.length !== 6) { return }
      busy.value = true
      codeError.value = ''
      const body = { userId: props.challenge?._id, verificationToken: props.challenge?.verificationToken, code: code.value }
      if (trustDevice.value) { body.trustDevice = true }
      await api
        .post('auth/loginMFA', body, { noRetry: true }) // a code works once: a resend after a dropped reply is refused
        .then(res => {
          if (typeof res.data === 'object' && res.data._id && res.data.token) { emit('signedIn', res.data) }
        })
        .catch(err => {
          const reason = err.response?.data?.code
          codeError.value = reason === 'MFA_EXPIRED' ? 'That code has expired. Send a new one.'
            : reason === 'MFA_LOCKED' ? (err.response?.data?.error || 'Too many attempts. Try again later.')
            : reason === 'MFA_INVALID' ? 'That code didn’t work. Check it and try again.'
            // A lock, deactivation or rate limit while on this step: say why, or the user keeps retyping a good code
            : (err.response?.data?.error || 'That code didn’t work. Check it and try again.')
          nextTick(() => codeInput.value?.select())
        })
        .finally(() => { busy.value = false })
    }

    const resend = async (channel) => {
      busy.value = true
      failedError.value = ''
      const body = { userId: props.challenge?._id, verificationToken: props.challenge?.verificationToken }
      if (channel === 'email') { body.channel = 'email' }
      await api
        .post('auth/resendMFA', body, { noRetry: true }) // a resend after a dropped reply would send a second code
        .then(res => {
          const fields = { ...(res.data || {}) }
          delete fields.success
          emit('update:challenge', { ...props.challenge, ...fields })
          code.value = ''
          codeError.value = ''
          resendNotice.value = ''
          startCountdown(fields.resendAfter)
          nextTick(() => codeInput.value?.focus())
        })
        .catch(err => {
          const status = err.response?.status
          const message = err.response?.data?.error || err.message
          if (status === 429) {
            resendNotice.value = message
            startCountdown(err.response?.data?.retryAfter)
          } else if (status === 403 && err.response?.data?.code === 'MFA_EXPIRED') {
            signInExpired.value = message
          } else if (view.value === 'failed') {
            failedError.value = message
          } else {
            codeError.value = message
          }
        })
        .finally(() => { busy.value = false })
    }

  // Helpers
    const stopCountdown = () => { if (countdown) { clearInterval(countdown); countdown = null } }
    const startCountdown = (seconds) => {
      stopCountdown()
      resendIn.value = typeof seconds === 'number' && seconds > 0 ? Math.ceil(seconds) : 0
      if (resendIn.value > 0) {
        countdown = setInterval(() => {
          resendIn.value = Math.max(0, resendIn.value - 1)
          if (resendIn.value === 0) { stopCountdown() }
        }, 1000)
      }
    }
    const resetForChallenge = () => {
      code.value = ''
      codeError.value = ''
      resendNotice.value = ''
      trustDevice.value = false
      startCountdown(props.challenge?.resendAfter)
      nextTick(() => codeInput.value?.focus())
    }

  // Watchers
    watch(() => props.challenge?.verificationToken, (token, previous) => { if (token && token !== previous) { resetForChallenge() } })

  // Lifecycle
    onMounted(resetForChallenge)
    onBeforeUnmount(stopCountdown)

    return {
      code, codeError, failedError, resendNotice, trustDevice, busy, signInExpired, resendIn, resendClock, resendLabel,
      codeInput, isSms, view, expiresIn, verify, resend,
      MailIcon, DeviceMobileIcon, ExclamationCircleOutline
    }
  }
}
</script>
